// Public landing (apex/www), ported from design/clearedto-rent/Landing.dc.html
// (the canonical pixel source; copy is verbatim from the comp). The finder's
// results dropdown needs a name-search endpoint that does not exist yet, so
// typing shows the drawn no-results card; the dropdown lands with that
// endpoint. Copy guard: tests/ctr-landing-copy.test.js.
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api.js';
import Footer from '@/components/Footer.jsx';
import Wordmark from '@/components/Wordmark.jsx';
import StatusPill from '@/components/ui/StatusPill.jsx';

const HERO_ROSTER = [
  { name: 'Maya Okafor', sub: 'Student pilot · via portal', status: 'cleared', pill: 'Cleared' },
  { name: 'Ethan Brooks', sub: 'Private pilot · outside policy', status: 'outside', pill: 'Outside · Cleared' },
  { name: 'Priya Raman', sub: 'Private pilot · renter', status: 'expiring', pill: 'Expiring · 12 days' },
  { name: 'Caleb Ng', sub: 'Student pilot', status: 'gap', pill: 'Gap · lapsed' },
];

const SCHOOL_POINTS = [
  'Live roster of every covered renter and CFI, even ones insured elsewhere',
  'COIs auto-issued and auto-filed the moment a renter buys',
  'Expirations flagged 30 days out, lapses flagged same day',
  "Renters can't buy less than your minimums",
  'Free. No contracts, no software fees, no catch',
];

const RENTER_POINTS = [
  "Your school or club's minimums, preselected for you",
  "Deductible reimbursement, so a hangar rash isn't a $5,000 day",
  'Your certificate goes to your school or club automatically',
  'Auto-renews so you never get grounded at the counter',
];

const STEPS = [
  ['Set your requirements once', "Liability limits, aircraft damage coverage, deductible reimbursement. Whatever you already require, configured as the minimum on every renter's quote."],
  ['Point renters at your portal', 'A QR poster for the front desk and a link for your enrollment packet or member handbook. Renters buy self-service in about 2 minutes. Premiums are collected securely by Global Aerospace.'],
  ['Watch the folder disappear', 'Every purchase files its own certificate on your dashboard. Renewals happen automatically. You get flagged when anything needs attention, and only then.'],
];

const FEATURES = [
  ['Certificates that file themselves', "A binder appears on your dashboard the moment a renter pays; Global's certificate, named to your organization, follows automatically."],
  ['Live covered roster', 'One screen answers "who is cleared to rent right now," including renters who bought coverage elsewhere. Log any COI and it\'s tracked the same way.'],
  ['Gap alerts', 'A renter flying without current coverage is your exposure. We flag lapses the day they happen.'],
  ['Requirements enforcement', "Your minimums are the starting point of every quote. Below them isn't an option a renter can click."],
  ['Automatic renewal', 'Policies and certificates stay current without anyone lifting a finger, yours or theirs.'],
  ['Your brand on it', "Your own portal page, your logo, your requirements. Renters and members know they're in the right place."],
];

const COMPARE = [
  ['Who tracks the COIs', 'Still you, by hand', 'Nobody. They file themselves'],
  ['Wrong-coverage purchases', 'Happens constantly', 'Impossible by construction'],
  ['Lapse visibility', 'You find out never', 'Flagged same day'],
  ['Your requirements', "Renter's problem", 'Preloaded in every quote'],
  ['Cost to you', '$0', '$0, and hours back every month'],
];

function portalUrlFor(slug) {
  const host = window.location.hostname.toLowerCase();
  if (host === 'clearedto.rent' || host.endsWith('.clearedto.rent')) return `https://${slug}.clearedto.rent`;
  return `/?school=${encodeURIComponent(slug)}`;
}

export default function Landing() {
  const [query, setQuery] = useState('');
  const [finderHits, setFinderHits] = useState([]);
  const [navOpen, setNavOpen] = useState(false);
  const seq = useRef(0);
  const searching = query.trim().length >= 2;

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setFinderHits([]); return undefined; }
    const my = ++seq.current;
    const t = setTimeout(() => {
      api.get(`/api/ctr/schools/search?q=${encodeURIComponent(q)}`)
        .then((r) => { if (seq.current === my) setFinderHits(r.schools || []); })
        .catch(() => { if (seq.current === my) setFinderHits([]); });
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  return (
    <div className="mx-auto min-h-screen max-w-[1240px] bg-white shadow-shell">
      <nav className="sticky top-0 z-50 flex items-center justify-between border-b border-line bg-white/95 px-6 py-[18px] backdrop-blur lg:px-14">
        <Wordmark className="text-[21px] text-primary" />
        <div className="hidden gap-7 text-sm font-semibold text-ink-soft md:flex">
          <a href="#schools" className="text-ink-soft hover:text-accent">For flight schools &amp; flying clubs</a>
          <Link to="/quote" className="text-ink-soft hover:text-accent">For pilots</Link>
          <a href="#how" className="text-ink-soft hover:text-accent">How it works</a>
        </div>
        <div className="flex items-center gap-4">
          <Link to="/signin" className="hidden text-sm font-semibold text-ink-soft hover:text-accent md:block">Sign in</Link>
          <Link to="/signup" className="hidden rounded-card bg-primary px-5 py-2.5 text-sm font-semibold text-white hover:bg-primary-deep md:block">
            Set up my portal
          </Link>
          <button type="button" aria-label="Menu" onClick={() => setNavOpen((v) => !v)}
            className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-card border-[1.5px] border-line text-lg text-ink md:hidden">
            {navOpen ? '×' : '☰'}
          </button>
        </div>
      </nav>
      {navOpen ? (
        <div className="sticky top-[73px] z-40 flex flex-col border-b border-line bg-white px-6 py-2 shadow-card md:hidden">
          <a href="#schools" onClick={() => setNavOpen(false)} className="min-h-[46px] py-3 text-[15px] font-semibold text-ink-soft">For schools</a>
          <Link to="/quote" className="min-h-[46px] py-3 text-[15px] font-semibold text-ink-soft">For pilots</Link>
          <Link to="/signin" className="min-h-[46px] py-3 text-[15px] font-semibold text-ink-soft">Sign in</Link>
          <Link to="/signup" className="mb-2 mt-1 rounded-card bg-primary px-5 py-3 text-center text-[15px] font-semibold text-white">Set up my portal</Link>
        </div>
      ) : null}

      <header className="grid items-center gap-14 bg-gradient-to-b from-white to-paper px-6 py-16 lg:grid-cols-[1.05fr_0.95fr] lg:px-14 lg:pb-[76px] lg:pt-[84px]">
        <div>
          <div className="mb-[18px] text-[13px] font-bold uppercase tracking-[0.12em] text-accent">
            Renter &amp; CFI insurance, built around your flight school or flying club
          </div>
          <h1 className="mb-5 text-[34px] font-bold leading-[1.06] tracking-[-0.03em] lg:text-[54px]">
            <span className="lg:whitespace-nowrap">Your renters, covered.</span><br />
            <span className="lg:whitespace-nowrap">Your <em className="not-italic text-accent">COI folder, gone.</em></span>
          </h1>
          <p className="mb-[34px] max-w-[520px] text-lg leading-[1.6] text-ink-dim">
            Every renter who buys through your school or club&apos;s portal is covered to your exact requirements,
            and their certificate of insurance files itself on your dashboard. Automatically. Forever.
          </p>
          <div className="flex flex-wrap items-center gap-3.5">
            <Link to="/signup" className="rounded-card bg-primary px-7 py-[15px] text-[15px] font-semibold text-white hover:bg-primary-deep">
              Set up my portal
            </Link>
            <Link to="/quote" className="rounded-card border-[1.5px] border-line-strong bg-white px-[26px] py-[13px] text-[15px] font-semibold text-primary hover:border-primary">
              Renter or CFI? Get your quote
            </Link>
          </div>
          <div className="mt-[30px] flex flex-wrap items-center gap-2.5 text-[13px] text-ink-faint">
            <span>Underwritten by Global Aerospace</span><span className="text-line-strong">·</span>
            <span>Binder on file the moment payment clears</span><span className="text-line-strong">·</span>
            <span>No cost to schools</span>
          </div>
        </div>

        <div className="relative hidden min-h-[500px] lg:block">
          <div className="absolute -bottom-[26px] -right-1 z-[2] w-[270px] rotate-2 rounded-panel border border-line bg-white p-[18px_20px] shadow-float">
            <div className="mb-2.5 flex items-center justify-between">
              <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-ink-faint">Certificate of insurance</span>
              <span className="rounded-pill bg-ok-bg px-2 py-[3px] text-[10px] font-bold text-ok-fg">FILED</span>
            </div>
            <div className="text-sm font-bold">Maya Okafor</div>
            <div className="mb-2.5 mt-0.5 text-[11.5px] text-ink-dim">Certificate holder: Northerly Island Flight Academy LLC</div>
            <div className="grid grid-cols-2 gap-1.5 text-[11px] text-ink-soft">
              <div className="rounded-[7px] bg-paper p-[7px_9px]">Liability<br /><b className="text-xs">$1,000,000</b></div>
              <div className="rounded-[7px] bg-paper p-[7px_9px]">Deductible reimb.<br /><b className="text-xs">$5,000</b></div>
            </div>
            <div className="mt-2.5 text-[10.5px] text-ink-faint">Filed automatically · today, 11:32 AM</div>
          </div>
          <div className="relative z-[1] w-[380px] max-w-[88%] overflow-hidden rounded-modal border border-line bg-white shadow-hero">
            <div className="flex items-center justify-between border-b border-line p-[14px_18px]">
              <div className="flex items-center gap-2.5">
                <div className="flex h-[30px] w-[30px] items-center justify-center rounded-[8px] bg-primary text-xs font-bold text-white">NI</div>
                <div>
                  <div className="text-[13px] font-bold">Northerly Island Flight Academy</div>
                  <div className="text-[10.5px] text-ink-faint">Covered roster</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-[22px] font-bold tracking-[-0.02em] text-ok-fg">47</div>
                <div className="text-[9.5px] font-bold uppercase tracking-[0.06em] text-ink-faint">Cleared to rent</div>
              </div>
            </div>
            {HERO_ROSTER.map((r) => (
              <div key={r.name} className="flex items-center justify-between border-b border-fill p-[11px_18px]">
                <div>
                  <div className="text-[13px] font-semibold">{r.name}</div>
                  <div className="text-[11px] text-ink-faint">{r.sub}</div>
                </div>
                <StatusPill status={r.status}>{r.pill}</StatusPill>
              </div>
            ))}
            <div className="p-[11px_18px] text-[11px] text-ink-faint">
              Renewals happen automatically. You get flagged only when something needs you.
            </div>
          </div>
        </div>
      </header>

      <section id="schools" className="grid gap-6 bg-paper px-6 py-16 lg:grid-cols-2 lg:px-14">
        <div className="flex flex-col rounded-modal border border-line bg-white p-9 shadow-card">
          <div className="mb-3 text-[11px] font-bold uppercase tracking-[0.1em] text-accent">For flight schools &amp; flying clubs</div>
          <h3 className="mb-2.5 text-2xl font-bold tracking-[-0.02em]">Stop collecting certificates. Start seeing them.</h3>
          <p className="mb-[18px] text-[15px] leading-[1.6] text-ink-dim">
            You didn&apos;t start a flight school or flying club to chase paperwork. Set your renter requirements once.
            From then on, every renter who buys through your portal is compliant by construction.
          </p>
          <div className="mb-6 flex flex-col gap-0.5">
            {SCHOOL_POINTS.map((p) => (
              <div key={p} className="flex items-start gap-2.5 py-1.5 text-[14.5px] leading-[1.5] text-ink-soft">
                <span className="flex-none font-bold text-ok-fg">✓</span><span>{p}</span>
              </div>
            ))}
          </div>
          <Link to="/signup" className="mt-auto self-start rounded-card bg-primary px-6 py-[13px] text-[15px] font-semibold text-white hover:bg-primary-deep">
            Get your portal
          </Link>
        </div>
        <div className="flex flex-col rounded-modal border border-line bg-white p-9 shadow-card">
          <div className="mb-3 text-[11px] font-bold uppercase tracking-[0.1em] text-accent">For renters &amp; CFIs</div>
          <h3 className="mb-2.5 text-2xl font-bold tracking-[-0.02em]">Covered in about 2 minutes. Guaranteed to satisfy your school or club.</h3>
          <p className="mb-[18px] text-[15px] leading-[1.6] text-ink-dim">
            Your quote comes preloaded with your school or club&apos;s exact requirements, so there&apos;s nothing to
            research and no chance of buying the wrong thing. Add more protection if you want it.
          </p>
          <div className="mb-6 flex flex-col gap-0.5">
            {RENTER_POINTS.map((p) => (
              <div key={p} className="flex items-start gap-2.5 py-1.5 text-[14.5px] leading-[1.5] text-ink-soft">
                <span className="flex-none font-bold text-ok-fg">✓</span><span>{p}</span>
              </div>
            ))}
          </div>
          <a href="#finder" className="mt-auto self-start rounded-card border-[1.5px] border-line-strong bg-white px-[22px] py-[11.5px] text-[15px] font-semibold text-primary hover:border-primary">
            Find your portal
          </a>
        </div>
      </section>

      <section id="how" className="px-6 py-[72px] text-center lg:px-14">
        <h2 className="mb-2.5 text-[28px] font-bold tracking-[-0.02em] lg:text-[34px]">How it works for your school or club</h2>
        <div className="mb-11 text-base text-ink-dim">Ten minutes of setup, then it runs itself.</div>
        <div className="grid gap-[22px] text-left md:grid-cols-3">
          {STEPS.map(([t, d], i) => (
            <div key={t} className="rounded-panel border border-line p-7">
              <div className="mb-4 flex h-[34px] w-[34px] items-center justify-center rounded-pill bg-primary text-[15px] font-bold text-white">{i + 1}</div>
              <h4 className="mb-2 text-[17px] font-bold">{t}</h4>
              <p className="text-sm leading-[1.6] text-ink-dim">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-primary px-6 py-16 text-center text-white lg:px-14">
        <h2 className="mb-2.5 text-[26px] font-bold tracking-[-0.02em] lg:text-[32px]">Built as compliance infrastructure, not an insurance ad</h2>
        <div className="mb-[42px] text-base text-teal-mute-dark">Everything a school or club needs to prove its renters are covered, with zero admin.</div>
        <div className="grid gap-[18px] text-left md:grid-cols-3">
          {FEATURES.map(([t, d]) => (
            <div key={t} className="rounded-card-lg border border-white/15 bg-white/[0.07] p-6">
              <h4 className="mb-[7px] text-[15.5px] font-bold">{t}</h4>
              <p className="text-[13.5px] leading-[1.55] text-teal-mute-dark">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-paper px-6 py-[72px] text-center lg:px-14">
        <h2 className="mb-2 text-[26px] font-bold tracking-[-0.02em] lg:text-[30px]">Pilots can buy renter insurance lots of places</h2>
        <div className="mb-9 text-[15px] text-ink-dim">Every carrier and app sells to the pilot. The difference is what your school or club gets.</div>
        <div className="mx-auto max-w-[780px] overflow-hidden rounded-panel border border-line bg-white text-left">
          <div className="grid grid-cols-[1.3fr_1fr_1fr] bg-fill text-xs font-bold uppercase tracking-[0.05em] text-ink-dim">
            <div className="p-[14px_20px]" /><div className="p-[14px_20px]">Any other carrier or app</div><div className="p-[14px_20px] text-primary">clearedto.rent</div>
          </div>
          {COMPARE.map(([k, them, us]) => (
            <div key={k} className="grid grid-cols-[1.3fr_1fr_1fr] border-t border-line text-sm">
              <div className="p-[14px_20px] font-semibold">{k}</div>
              <div className="p-[14px_20px] text-ink-faint">{them}</div>
              <div className="p-[14px_20px] font-semibold text-primary">{us}</div>
            </div>
          ))}
        </div>
      </section>

      <section id="finder" className="px-6 py-[72px] text-center lg:px-14">
        <h2 className="mb-2 text-[26px] font-bold tracking-[-0.02em] lg:text-[30px]">Renting or instructing? Find your portal.</h2>
        <div className="mb-[30px] text-[15px] text-ink-dim">Your school or club&apos;s requirements are already loaded. Type its name and we&apos;ll take you there.</div>
        <div className="relative mx-auto max-w-[520px] text-left">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder='Try "Northerly" or your airport code'
            className="w-full rounded-card-lg border-[1.5px] border-line-strong p-[16px_18px] text-base text-ink shadow-card outline-none focus:border-accent focus:shadow-ring"
          />
          {searching ? (
            <div className="absolute left-0 right-0 top-[60px] z-20 rounded-card-lg border border-line bg-white shadow-float">
              {finderHits.length ? (
                <div className="py-1">
                  {finderHits.map((s) => (
                    <a key={s.slug} href={portalUrlFor(s.slug)}
                      className="flex items-center justify-between gap-2 px-4 py-2.5 hover:bg-paper">
                      <span className="min-w-0">
                        <span className="block truncate text-[13.5px] font-semibold text-ink">{s.name}</span>
                        <span className="text-[11px] text-ink-faint">{[s.home_airport, s.state].filter(Boolean).join(' · ')}</span>
                      </span>
                      <span className={`flex-none rounded-pill px-2 py-[3px] text-[10px] font-bold uppercase tracking-[0.04em] ${s.claimed ? 'bg-teal-fill text-primary' : 'bg-fill text-ink-dim'}`}>
                        {s.claimed ? 'portal live' : 'listed'}
                      </span>
                    </a>
                  ))}
                </div>
              ) : (
                <div className="p-4">
                  <div className="text-sm font-semibold">No school or club by that name yet</div>
                  <div className="mt-1 text-[13px] leading-[1.5] text-ink-dim">
                    Can&apos;t find your school? <Link to="/quote" className="font-semibold text-accent">Get a quote anyway</Link>, and
                    tell us where you rent. Or <Link to="/signup">send them this page</Link> so they get a portal.
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </section>

      <section className="border-t border-line px-6 py-12 lg:px-14">
        <div className="mx-auto flex max-w-[780px] flex-wrap items-center justify-between gap-5 rounded-modal border border-teal-line bg-teal-faint p-[26px_30px]">
          <div className="min-w-0">
            <div className="mb-1 text-[11px] font-bold uppercase tracking-[0.1em] text-accent">For pilots</div>
            <div className="text-[19px] font-bold tracking-[-0.01em]">Not at a partner school? You can still get covered in about two minutes.</div>
          </div>
          <Link to="/quote" className="flex-none rounded-card bg-primary px-6 py-[13px] text-[15px] font-semibold text-white hover:bg-primary-deep">
            Renter or CFI? Get your quote
          </Link>
        </div>
      </section>

      <section id="cta" className="border-t border-line bg-gradient-to-b from-white to-paper px-6 py-[76px] text-center lg:px-14">
        <h2 className="mb-3 text-[28px] font-bold tracking-[-0.03em] lg:text-[34px]">Put your COI folder out of business</h2>
        <p className="mb-7 text-base text-ink-dim">Set up takes ten minutes. Your first renter can be covered this afternoon.</p>
        <Link to="/signup" className="inline-block rounded-card bg-primary px-8 py-4 text-base font-semibold text-white hover:bg-primary-deep">
          Set up my portal
        </Link>
        <div className="mt-4 text-[13px] text-ink-faint">Free to schools and clubs. No contracts, no software fees, no catch.</div>
      </section>

      <Footer />
    </div>
  );
}
